interface Problem {
  id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  tags: string[];
  starterCode: string;
  solution: string;
  hints: string[];
}

export const recursionProblems: Problem[] = [
  {
    id: 'rec-1',
    title: "Sum of Array Elements",
    description: "Write a recursive function that calculates the sum of all elements in an array. Do not use any loops or built-in array methods like reduce.\n\n**Example:**\n```\ninput: [1, 2, 3, 4, 5]\noutput: 15\n```\n\n**Constraints:**\n- The array can contain both positive and negative integers\n- You must use recursion to solve this problem",
    difficulty: "Easy",
    tags: ['array', 'basic recursion', 'sum'],
    starterCode: `function arraySum(arr) {
  // Your code here
  return 0;
}`,
    solution: `function arraySum(arr) {
  // Base case: empty array
  if (arr.length === 0) return 0;
  
  // Recursive case: first element + sum of rest
  return arr[0] + arraySum(arr.slice(1));
}`,
    hints: [
      "First identify your base case - what happens when the array is empty?",
      "For the recursive case, think about breaking the problem into smaller parts",
      "Consider how to combine the first element with the sum of the remaining elements"
    ]
  },
  {
    id: 'rec-2',
    title: "Binary Search",
    description: "Implement a recursive binary search function that returns the index of a target element in a sorted array. Return -1 if the element is not found.\n\n**Example:**\n```\ninput: arr = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], target = 7\noutput: 6  // index of 7 in the array\n```\n\n**Constraints:**\n- The array is sorted in ascending order\n- You must use recursion to solve this problem",
    difficulty: "Medium",
    tags: ['array', 'binary search', 'divide and conquer'],
    starterCode: `function binarySearch(arr, target, start = 0, end = arr.length - 1) {
  // Your code here
  return -1;
}`,
    solution: `function binarySearch(arr, target, start = 0, end = arr.length - 1) {
  // Base case: element not found
  if (start > end) return -1;
  
  const mid = Math.floor((start + end) / 2);
  
  // Found the element
  if (arr[mid] === target) return mid;
  
  // Recursive cases
  if (arr[mid] > target) {
    return binarySearch(arr, target, start, mid - 1);
  } else {
    return binarySearch(arr, target, mid + 1, end);
  }
}`,
    hints: [
      "Find the middle element and compare it with the target",
      "If the middle element is the target, return its index",
      "If the target is less than the middle element, search the left half",
      "If the target is greater than the middle element, search the right half"
    ]
  },
  {
    id: 'rec-3',
    title: "Generate All Permutations",
    description: "Write a recursive function that generates all possible permutations of a string. For example, for input 'abc', it should return ['abc', 'acb', 'bac', 'bca', 'cab', 'cba'].\n\n**Example:**\n```\ninput: 'abc'\noutput: ['abc', 'acb', 'bac', 'bca', 'cab', 'cba']\n```\n\n**Constraints:**\n- The input string will only contain lowercase letters\n- There will be no duplicate characters in the input string",
    difficulty: "Hard",
    tags: ['string', 'permutation', 'backtracking'],
    starterCode: `function generatePermutations(str) {
  // Your code here
  return [];
}`,
    solution: `function generatePermutations(str) {
  // Base case: single character
  if (str.length <= 1) return [str];
  
  const result = [];
  
  // Try each character as the first character
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    // Get all permutations of the remaining characters
    const remainingChars = str.slice(0, i) + str.slice(i + 1);
    const subPermutations = generatePermutations(remainingChars);
    
    // Add current char to the beginning of each sub-permutation
    for (const subPerm of subPermutations) {
      result.push(char + subPerm);
    }
  }
  
  return result;
}`,
    hints: [
      "Start with the base case: a string of length 1 has only one permutation",
      "For longer strings, try each character as the first character, then recursively find permutations of the remaining characters",
      "Think about how to combine each character with the permutations of the remaining characters"
    ]
  },
  {
    id: 'rec-4',
    title: "Factorial Calculation",
    description: "Write a recursive function to calculate the factorial of a non-negative integer n. The factorial of n is the product of all positive integers less than or equal to n.\n\n**Example:**\n```\ninput: 5\noutput: 120  // 5! = 5 * 4 * 3 * 2 * 1 = 120\n```\n\n**Constraints:**\n- 0 ≤ n ≤ 12 (to avoid integer overflow)",
    difficulty: "Easy",
    tags: ['math', 'basic recursion'],
    starterCode: `function factorial(n) {
  // Your code here
  return 0;
}`,
    solution: `function factorial(n) {
  // Base case
  if (n === 0 || n === 1) return 1;
  
  // Recursive case
  return n * factorial(n - 1);
}`,
    hints: [
      "Identify the base case: factorial of 0 or 1 is 1",
      "For the recursive case, think about how n! relates to (n-1)!",
      "Remember to handle edge cases, such as negative numbers"
    ]
  },
  {
    id: 'rec-5',
    title: "Fibonacci Sequence",
    description: "Write a recursive function to find the nth Fibonacci number. The Fibonacci sequence starts with 0 and 1, and each subsequent number is the sum of the two preceding ones.\n\n**Example:**\n```\ninput: n = 6\noutput: 8  // Fibonacci sequence: 0, 1, 1, 2, 3, 5, 8, 13, ...\n```\n\n**Constraints:**\n- 0 ≤ n ≤ 30 (to avoid integer overflow)\n- Implement a memoization strategy to avoid redundant calculations",
    difficulty: "Medium",
    tags: ['math', 'memoization', 'dynamic programming'],
    starterCode: `function fibonacci(n, memo = {}) {
  // Your code here
  return 0;
}`,
    solution: `function fibonacci(n, memo = {}) {
  // Check if already calculated
  if (n in memo) return memo[n];
  
  // Base cases
  if (n <= 1) return n;
  
  // Calculate and store in memo
  memo[n] = fibonacci(n - 1, memo) + fibonacci(n - 2, memo);
  return memo[n];
}`,
    hints: [
      "Start with the base cases: F(0) = 0 and F(1) = 1",
      "For any n > 1, F(n) = F(n-1) + F(n-2)",
      "Use memoization to store already calculated values to avoid redundant calculations",
      "A naive recursive solution without memoization will be too slow for larger values of n"
    ]
  },
  {
    id: 'rec-6',
    title: "Palindrome Check",
    description: "Write a recursive function to determine if a string is a palindrome. A palindrome reads the same backward as forward.\n\n**Example:**\n```\ninput: 'racecar'\noutput: true\n\ninput: 'hello'\noutput: false\n```\n\n**Constraints:**\n- The input will be a single word (no spaces)\n- Ignore case when comparing characters",
    difficulty: "Easy",
    tags: ['string', 'basic recursion'],
    starterCode: `function isPalindrome(str) {
  // Your code here
  return false;
}`,
    solution: `function isPalindrome(str) {
  // Convert to lowercase
  str = str.toLowerCase();
  
  // Base cases
  if (str.length <= 1) return true;
  
  // Check first and last characters
  if (str[0] !== str[str.length - 1]) return false;
  
  // Recursive case: check the substring without first and last characters
  return isPalindrome(str.slice(1, str.length - 1));
}`,
    hints: [
      "Base case: an empty string or a string with one character is a palindrome",
      "Compare the first and last characters of the string",
      "If they match, recursively check if the substring (without the first and last characters) is a palindrome",
      "Don't forget to handle case sensitivity"
    ]
  },
  {
    id: 'rec-7',
    title: "Recursive Power Function",
    description: "Write a recursive function to calculate x raised to the power n (x^n).\n\n**Example:**\n```\ninput: x = 2, n = 3\noutput: 8  // 2^3 = 2 * 2 * 2 = 8\n```\n\n**Constraints:**\n- -100.0 < x < 100.0\n- -10 <= n <= 10\n- Use a more efficient approach than multiplying x by itself n times",
    difficulty: "Medium",
    tags: ['math', 'divide and conquer'],
    starterCode: `function power(x, n) {
  // Your code here
  return 0;
}`,
    solution: `function power(x, n) {
  // Handle negative exponents
  if (n < 0) return 1 / power(x, -n);
  
  // Base cases
  if (n === 0) return 1;
  if (n === 1) return x;
  
  // Recursive case with optimization for even powers
  if (n % 2 === 0) {
    const half = power(x, n / 2);
    return half * half;
  } else {
    return x * power(x, n - 1);
  }
}`,
    hints: [
      "Consider the base cases: x^0 = 1 and x^1 = x",
      "For negative powers, use the relationship: x^(-n) = 1 / (x^n)",
      "For optimization, use the fact that x^n = (x^(n/2))^2 when n is even",
      "When n is odd, x^n = x * x^(n-1)"
    ]
  },
  {
    id: 'rec-8',
    title: "Tower of Hanoi",
    description: "Implement a recursive solution to the Tower of Hanoi puzzle. The goal is to move a stack of n disks from the source rod to the target rod, using an auxiliary rod, following these rules:\n- Only one disk can be moved at a time\n- No disk may be placed on top of a smaller disk\n\nYour function should print the sequence of moves to solve the puzzle.\n\n**Example:**\n```\ninput: n = 3 (3 disks)\noutput: \nMove disk 1 from source to target\nMove disk 2 from source to auxiliary\nMove disk 1 from target to auxiliary\nMove disk 3 from source to target\nMove disk 1 from auxiliary to source\nMove disk 2 from auxiliary to target\nMove disk 1 from source to target\n```",
    difficulty: "Hard",
    tags: ['recursion', 'puzzle', 'algorithm'],
    starterCode: `function towerOfHanoi(n, source = 'source', auxiliary = 'auxiliary', target = 'target') {
  // Your code here
}`,
    solution: `function towerOfHanoi(n, source = 'source', auxiliary = 'auxiliary', target = 'target') {
  // Base case
  if (n === 1) {
    console.log(\`Move disk 1 from \${source} to \${target}\`);
    return;
  }
  
  // Move n-1 disks from source to auxiliary, using target as auxiliary
  towerOfHanoi(n - 1, source, target, auxiliary);
  
  // Move the nth disk from source to target
  console.log(\`Move disk \${n} from \${source} to \${target}\`);
  
  // Move n-1 disks from auxiliary to target, using source as auxiliary
  towerOfHanoi(n - 1, auxiliary, source, target);
}`,
    hints: [
      "Break down the problem: to move n disks, first move n-1 disks to the auxiliary rod",
      "Then move the largest disk to the target rod",
      "Finally, move the n-1 disks from the auxiliary rod to the target rod",
      "The base case is moving 1 disk, which is straightforward"
    ]
  },
  {
    id: 'rec-9',
    title: "Recursive Path Finding in a Matrix",
    description: "Write a recursive function that finds all possible paths from the top-left corner to the bottom-right corner of a matrix. You can only move right or down at each step.\n\n**Example:**\n```\ninput: 3x3 matrix\noutput: 6 different paths\n```\n\n**Constraints:**\n- The matrix dimensions are given as m x n, where 1 ≤ m, n ≤ 10\n- Return the number of possible paths",
    difficulty: "Medium",
    tags: ['matrix', 'path finding', 'dynamic programming'],
    starterCode: `function countPaths(m, n, memo = {}) {
  // Your code here
  return 0;
}`,
    solution: `function countPaths(m, n, memo = {}) {
  // Create a key for memoization
  const key = m + ',' + n;
  
  // Check if already calculated
  if (key in memo) return memo[key];
  
  // Base cases
  if (m === 1 || n === 1) return 1;
  
  // Recursive case with memoization
  memo[key] = countPaths(m - 1, n, memo) + countPaths(m, n - 1, memo);
  return memo[key];
}`,
    hints: [
      "If you're at the first row or first column, there's only one way to reach that position",
      "For any other position, the number of ways is the sum of ways to reach the position above and to the left",
      "Use memoization to avoid recalculating the same subproblems",
      "Think of this as a dynamic programming problem with a recursive solution"
    ]
  },
  {
    id: 'rec-10',
    title: "Recursive Merge Sort",
    description: "Implement the merge sort algorithm recursively to sort an array. Merge sort is a divide-and-conquer algorithm that works by dividing the array into smaller subarrays, sorting them, and then merging them back together.\n\n**Example:**\n```\ninput: [38, 27, 43, 3, 9, 82, 10]\noutput: [3, 9, 10, 27, 38, 43, 82]\n```\n\n**Constraints:**\n- The array can contain both positive and negative integers\n- You must implement both the mergeSort function and the merge helper function",
    difficulty: "Medium",
    tags: ['array', 'sorting', 'divide and conquer'],
    starterCode: `function mergeSort(arr) {
  // Your code here
  return arr;
}

function merge(left, right) {
  // Your code here
  return [];
}`,
    solution: `function mergeSort(arr) {
  // Base case
  if (arr.length <= 1) return arr;
  
  // Divide array in half
  const mid = Math.floor(arr.length / 2);
  const left = mergeSort(arr.slice(0, mid));
  const right = mergeSort(arr.slice(mid));
  
  // Merge the sorted halves
  return merge(left, right);
}

function merge(left, right) {
  let result = [];
  let leftIndex = 0;
  let rightIndex = 0;
  
  // Compare elements from both arrays and add the smaller one to result
  while (leftIndex < left.length && rightIndex < right.length) {
    if (left[leftIndex] < right[rightIndex]) {
      result.push(left[leftIndex]);
      leftIndex++;
    } else {
      result.push(right[rightIndex]);
      rightIndex++;
    }
  }
  
  // Add remaining elements
  return result.concat(left.slice(leftIndex)).concat(right.slice(rightIndex));
}`,
    hints: [
      "Divide the array into two halves, recursively sort each half, then merge them",
      "The base case is an array with 0 or 1 elements, which is already sorted",
      "Implement a merge function to combine two sorted arrays into one sorted array",
      "In the merge function, compare elements from both arrays and add the smaller one to the result"
    ]
  }
];
